/**
 * @file DownloadsPage.js
 * @module src/js/pages/DownloadsPage
 * @description
 * **Renders the public Downloads page.**
 * - Fetches the list of released software/benchmarks (and their files) from `/api/downloads`.
 * - Hierarchical, mirroring the legacy `/w04/download` page: a top-level list of titles,
 *   each expandable in place to reveal its description and download links (the legacy site
 *   used a separate page per title; this collapses that into an in-page accordion instead
 *   of introducing dynamic routing, which isn't used elsewhere in this app).
 * - Styled consistently with the rest of the new site (theme-aware wrapper, Markdown descriptions).
 * - Publicly accessible: no login is required to view or download.
 *
 * @version
 * <p>
 *  2026-07-12: First version, replacing the placeholder stub. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2026-07-12: Made hierarchical: titles collapse/expand to reveal details, like the legacy page. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2026-07-12: Group files under sub-headings (Programs, Benchmarks, ...) mirroring the legacy sections. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2026-07-12: Use relative download URLs (same origin) so downloads work without forwarding port 5002 in dev. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2026-07-12: Build download URLs from the file's real relative path, mirroring the legacy site's URLs. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2026-07-13: Add the legacy copyright / academic-use notice as a page footer. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2026-07-13: Skip files already linked inline in the description so they aren't listed twice. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2026-07-14: Use the `DownloadLink` component to download files (forwarded to port 5002). (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2026-10-01: Nav-style accordion on section titles; interaction styles in index.css. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useCallback, useEffect, useState } from "react";
import useFetchFromBackend from "../hooks/useFetchFromBackend";
import ThemeToggledWrapper from "../components/ThemeToggledWrapper";
import { ExtendedMarkdown, DownloadLink, LoadingThreeDots } from "../components/UtilityComponents";

// Group files by their groupLabel, preserving first-appearance order.
// Files with a null/empty groupLabel are collected under a single unlabeled group.
function groupFiles(files) {
    const groups = [];
    const indexByLabel = new Map();
    for (const file of files) {
        const key = file.groupLabel || "";
        if (!indexByLabel.has(key)) {
            indexByLabel.set(key, groups.length);
            groups.push({ label: file.groupLabel || null, files: [] });
        }
        groups[indexByLabel.get(key)].files.push(file);
    }
    return groups;
}

function DownloadSectionRow({ item, isExpanded, onToggle }) {
    const visibleFiles = item.files.filter(
        (f) => !(item.text && item.text.includes(f.filename))
    );

    return (
        <li className="has-submenu">
            <div
                className="downloads-section-title"
                role="button"
                tabIndex={0}
                onClick={onToggle}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onToggle();
                    }
                }}
            >
                <h3>
                    {item.title}
                    <i
                        className={`fa-solid fa-angle-${isExpanded ? "down" : "up"} downloads-section-chevron`}
                        aria-hidden
                    />
                </h3>
            </div>
            <div className={`submenu-${isExpanded ? "open" : "close"}`}>
                {item.text && <ExtendedMarkdown>{item.text}</ExtendedMarkdown>}
                {groupFiles(visibleFiles).map((group, gi) => (
                    <div key={gi} style={{ marginTop: "1em" }}>
                        {group.label && (
                            <b style={{ display: "block", marginBottom: "0.25em" }}>
                                {group.label}
                            </b>
                        )}
                        <ul>
                            {group.files.map((file) => (
                                <li key={file.fileID}>
                                    <DownloadLink
                                        file={`/static-files/downloads/${file.filename}`}
                                        requireAuth={false}
                                    >
                                        {file.label}
                                    </DownloadLink>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        </li>
    );
}

export default function DownloadsPage() {
    const fetchFromBackend = useFetchFromBackend();
    const [downloads, setDownloads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedDownloadID, setExpandedDownloadID] = useState(null);

    useEffect(() => {
        async function fetchData() {
            const data = await fetchFromBackend({ request: "/api/downloads" });
            setDownloads(data || []);
            setLoading(false);
        }
        fetchData();
    }, [fetchFromBackend]);

    const toggleExpanded = useCallback((downloadID) => {
        setExpandedDownloadID(prev => (prev === downloadID ? null : downloadID));
    }, []);

    return (
        <ThemeToggledWrapper id="downloads" lightStyle={5} darkStyle={4}>
            <div className="inner">
                <header className="major special">
                    <h2>Downloads</h2>
                    <p>Software and benchmarks released by the lab for academic use.</p>
                </header>

                {loading ? <LoadingThreeDots /> : (
                    <ul className="downloads-list">
                        {downloads.map((item) => (
                            <DownloadSectionRow
                                key={item.downloadID}
                                item={item}
                                isExpanded={expandedDownloadID === item.downloadID}
                                onToggle={() => toggleExpanded(item.downloadID)}
                            />
                        ))}
                    </ul>
                )}

                {/* Copyright / academic-use notice, carried over verbatim from the
                    legacy download page's footer. */}
                <footer style={{ marginTop: "3em", fontSize: "0.8em", opacity: 0.8, lineHeight: 1.4 }}>
                    <hr />
                    <p style={{ margin: 0 }}>Copyright &copy; {new Date().getFullYear()} Electronics Design Automation Laboratory. All Rights Reserved.</p>
                    <p style={{ margin: 0 }}>Unless otherwise indicated, all materials on these pages are copyrighted by the Electronics Design Automation Laboratory. All rights reserved.</p>
                    <p style={{ margin: 0 }}>No part of these pages, either source codes or executables may be used for any purpose other than academic use.</p>
                    <p style={{ margin: 0 }}>Usage from any personnel of any business is considered commercial use.</p>
                    <p style={{ margin: 0 }}>Therefore, reproduction, modification, storage in a retrieval system or retransmission, in any form or by any means, electronic, mechanical or otherwise, for reasons other than academic use, is strictly prohibited without prior written permission.</p>
                </footer>
            </div>
        </ThemeToggledWrapper>
    );
}
